/* eslint-disable jsx-a11y/iframe-has-title */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { useLocation } from "react-router";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faSpinner } from "@fortawesome/free-solid-svg-icons";

import Header from "../Components/Header";
import Footer from "../Components/Footer";

import { getLocalData } from "../utils/LocalStorage";
import AddressInput from "../Components/AddressInput";
import SpaceGridItem from "../Components/SpaceGridItem";
import SearchFilters from "../Components/SearchFilters";
import { SearchFiltersType } from "../Constants/Interfaces";
import MapViewComponent from "../Components/MapViewComponent";
import SpotDetailsModal from "../Components/Modals/SpotDetailsModal";
import { getSearchSpacesAction } from "../Services/Store/Actions/SpaceActions";

const Search = () => {
  const userLocation = getLocalData("userLocation");

  const location = useLocation();

  const dispatch = useDispatch();
  const firstRender = useRef(true);
  const { searchSpaces, agentFilters } = useSelector(
    (state: any) => state.spaces
  );

  const [loading, setLoading] = useState(false);
  const [showDetails, setShowDetails] = useState({ show: false, item: null });
  const [pagination, setPagination] = useState<any>({});
  // Kept separate from `loading` so a Load More request never blanks the grid
  // that is already on screen.
  const [loadingMore, setLoadingMore] = useState(false);

  const [mapCenter, setMapCenter] = useState({});
  const [searchText, setSearchText] = useState("");
  const [markers, setMarkers] = useState([]);

  const [searchItems, setSearchItems] = useState([]);
  const [searchFilters, setSearchFilters] = useState<SearchFiltersType>({
    address: "",
    priceFrom: "",
    priceTo: "",
    date: "",
    startTime: "",
    endTime: "",
    duration: "",
    spaceType: "",
    capacity: "",
    customCapacity: "",
    amenities: [],
    vibes: [],
    features: [],
    occations: [],
  });

  const getSearchSpaceItems = useCallback(
    (page = 1, withFilters = false) => {
      // One request at a time: guards against double-clicking Load More.
      if (!loading && !loadingMore) {
        const searchParams = {
          page,
          limit: 6,
          type: "normal",
        };

        if (searchFilters && searchFilters.address) {
          searchParams.address = searchFilters.address;
        } else if (withFilters) {
          const addressText =
            location && location.state && location.state.address
              ? location.state.address
              : "";

          if (addressText) {
            searchParams.address = addressText;
          }
        }

        if (searchFilters && searchFilters.priceFrom && searchFilters.priceTo) {
          searchParams.minRate = searchFilters.priceFrom;
          searchParams.maxRate = searchFilters.priceTo;
        }

        if (
          searchFilters &&
          searchFilters.date &&
          searchFilters.startTime &&
          searchFilters.endTime
        ) {
          searchParams.startDate = moment(searchFilters.date).format(
            "YYYY-MM-DD",
          );
          searchParams.endDate = moment(searchFilters.date).format(
            "YYYY-MM-DD",
          );
          searchParams.startTime = searchFilters.startTime;
          searchParams.endTime = searchFilters.endTime;
        }

        // if (
        //   searchFilters &&
        //   searchFilters.date &&
        //   searchFilters.startTime &&
        //   searchFilters.endTime
        // ) {
        //   const selectedDate = moment(searchFilters.date).format("YYYY-MM-DD");
        //   searchParams.duration = getDurationInMinutes(
        //     selectedDate,
        //     searchFilters.startTime,
        //     searchFilters.endTime
        //   );
        // }

        // Milo emits raw people counts rather than the UI's bucket labels, so
        // honour explicit bounds when present. The bucket control below is
        // untouched and still drives manual filtering.
        if (
          searchFilters &&
          (searchFilters.minCapacity || searchFilters.maxCapacity)
        ) {
          if (searchFilters.minCapacity) {
            searchParams.minCapacity = searchFilters.minCapacity;
          }
          if (searchFilters.maxCapacity) {
            searchParams.maxCapacity = searchFilters.maxCapacity;
          }
        } else if (searchFilters && searchFilters.capacity) {
          if (searchFilters.capacity == "1 to 10") {
            searchParams.minCapacity = 1;
            searchParams.maxCapacity = 10;
          } else if (searchFilters.capacity == "11 to 25") {
            searchParams.minCapacity = 11;
            searchParams.maxCapacity = 25;
          } else if (searchFilters.capacity == "26 to 50") {
            searchParams.minCapacity = 26;
            searchParams.maxCapacity = 50;
          } else if (searchFilters.capacity == "51 to 100") {
            searchParams.minCapacity = 51;
            searchParams.maxCapacity = 100;
          } else if (searchFilters.capacity == "Over 100") {
            searchParams.minCapacity = 101;
            searchParams.maxCapacity = "";
          } else if (searchFilters.capacity == "Custom") {
            searchParams.minCapacity = searchFilters.customCapacity;
            searchParams.maxCapacity = "";
          }
        }

        if (searchFilters && searchFilters.name) {
          searchParams.name = searchFilters.name;
        }

        searchParams.tags = "";

        if (searchFilters && searchFilters.spaceType) {
          searchParams.tags = searchFilters.spaceType;
        }

        if (
          searchFilters &&
          searchFilters.vibes &&
          searchFilters.vibes.length !== 0
        ) {
          searchParams.tags = searchParams.tags
            ? `${searchParams.tags},${searchFilters.vibes.join(",")}`
            : searchFilters.vibes.join(",");
        } else if (
          withFilters &&
          searchFilters &&
          searchFilters.vibes &&
          searchFilters.vibes.length === 0
        ) {
          const vibeText =
            location.state && location.state.vibe ? location.state.vibe : "";

          if (vibeText) {
            searchParams.tags = `${searchParams.tags},${vibeText}`;
          }
        }

        if (
          searchFilters &&
          searchFilters.occations &&
          searchFilters.occations.length !== 0
        ) {
          // searchParams.occations = searchFilters.occations.join(",");

          searchParams.tags = searchParams.tags
            ? `${searchParams.tags},${searchFilters.occations.join(",")}`
            : searchFilters.occations.join(",");
        } else if (
          withFilters &&
          searchFilters.occations &&
          searchFilters.occations.length === 0
        ) {
          const occassionText =
            location.state && location.state.activity
              ? location.state.activity
              : "";
          if (occassionText) {
            searchParams.tags = `${searchParams.tags},${occassionText}`;
          }
        }

        if (
          searchFilters &&
          searchFilters.features &&
          searchFilters.features.length !== 0
        ) {
          searchParams.features = searchFilters.features.join(",");
        }

        if (
          searchFilters &&
          searchFilters.amenities &&
          searchFilters.amenities.length !== 0
        ) {
          searchParams.features = searchParams.features
            ? `${searchParams.features},${searchFilters.amenities.join(",")}`
            : searchFilters.amenities.join(",");
        }

        if (userLocation) {
          const loc = JSON.parse(userLocation);

          searchParams.lat = loc.lat || "";
          searchParams.lng = loc.lng || "";
        }

        const isFirstPage = page === 1;

        // Page 1 replaces the grid (full-page spinner); later pages append
        // underneath it, so we must not flip `loading` or the existing results
        // would be unmounted mid-scroll.
        if (isFirstPage) {
          setLoading(true);
        } else {
          setLoadingMore(true);
        }

        getSearchSpacesAction(searchParams, dispatch)
          .then((res) => {
            setPagination(res.pagination);
          })
          .catch((err) => {
            console.log("ERR :", err);
            // A failed Load More leaves the already-loaded pages untouched;
            // `pagination` keeps its previous nextPage so the user can retry.
          })
          .finally(() => {
            if (isFirstPage) {
              setLoading(false);
            } else {
              setLoadingMore(false);
            }
          });
      }
    },
    [searchFilters, dispatch, loading, loadingMore, setLoading],
  );

  // Milo publishes a normalised filter set; applying it to searchFilters makes
  // the existing effect below refetch page 1 and replace the grid, so the agent
  // and the manual controls share one search path and one result list.
  useEffect(() => {
    if (!agentFilters || !agentFilters.filters) return;

    setSearchFilters((prev: SearchFiltersType) => ({
      ...prev,
      ...agentFilters.filters,
    }));
    // appliedAt changes on every agent turn, so repeating a request re-applies.
  }, [agentFilters?.appliedAt]);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }

    console.log("searchFilters updated...", searchFilters);

    getSearchSpaceItems(1);
  }, [searchFilters, searchText]);

  useEffect(() => {
    if (searchSpaces) {
      const markers = [];

      for (let i = 0; i < searchSpaces.length; i++) {
        if (searchSpaces[i] && searchSpaces[i].location) {
          markers.push({
            item: searchSpaces[i],
            id: searchSpaces[i].id,
            title: searchSpaces[i].name,
            price: searchSpaces[i].ratePerHour,
            location: {
              lat: searchSpaces[i].location.coordinates[1],
              lng: searchSpaces[i].location.coordinates[0],
            },
          });
        }
      }
      const loc = JSON.parse(userLocation) || null;

      setMapCenter(markers.length > 0 ? markers[0].location : loc);
      setMarkers(markers);

      setSearchItems(searchSpaces);
    }
  }, [searchSpaces]);

  useEffect(() => {
    console.log("Location:", location);

    window.scrollTo({ top: 0, behavior: "smooth" });

    if (
      location &&
      location.state &&
      (location.state.activity ||
        location.state.vibe ||
        location.state.spaceType ||
        location.state.address)
    ) {
      setSearchFilters({
        ...searchFilters,
        address: location.state.address ? location.state.address : "",
        occations: location.state.activity ? [location.state.activity] : [],
        vibes: location.state.vibe ? [location.state.vibe] : [],
        spaceType: location.state.spaceType ? location.state.spaceType : "",
      });

      if (location.state.address) {
        setSearchText(location.state.address);
      }
    } else {
      getSearchSpaceItems(1, true);
    }
  }, []);

  return (
    <>
      <Header />

      <div className="container">
        <div className="fiters-wrapper">
          <div className="filters-header">
            <h3>Filter By</h3>
            <div className="search-form-input">
              <AddressInput
                placeHolder="Search by Location"
                address={searchText}
                onChange={(address: string) => setSearchText(address)}
                onAddressPicked={(address: string) =>
                  setSearchFilters({ ...searchFilters, address: address })
                }
              />

              <FontAwesomeIcon icon={faSearch} />
            </div>
          </div>

          <SearchFilters
            data={searchFilters}
            updateSearchData={(data: SearchFiltersType) =>
              setSearchFilters(data)
            }
          />
        </div>

        <div className="search-results-wrapper">
          {searchItems.length > 0 && (
            <div className="search-results-count">
              {searchItems.length} spaces nearby your location
            </div>
          )}

          <div className="row">
            <div className="col-md-5 col-sm-5">
              {/* The column itself is stretched to the row height by flexbox,
                  so the sticky element has to be this inner wrapper — that is
                  what gives the map room to travel as the cards scroll, and
                  what makes it stop with the results container rather than
                  running over the footer. */}
              <div className="search-map-sticky">
                <MapViewComponent
                  id="search-map"
                  mapHeight={650}
                  mapCenter={mapCenter}
                  markers={markers}
                  onMarkerClicked={(item: any) =>
                    setShowDetails({ show: true, item })
                  }
                />
              </div>
            </div>

            <div className="col-md-7 col-sm-7">
              <div className="row">
                {!loading && searchItems.length === 0 && (
                  <div className="col-12">
                    <div className="no-results">
                      <p>No Spaces available</p>
                    </div>
                  </div>
                )}

                {!loading &&
                  searchItems &&
                  searchItems.length > 0 &&
                  searchItems.map((item, index) => (
                    <div
                      key={`search-item-${item.id}-${index}`}
                      className="col-md-4 col-sm-6 col-xs-12"
                    >
                      <SpaceGridItem
                        item={item}
                        onClick={() => setShowDetails({ show: true, item })}
                      />
                    </div>
                  ))}

                {loading && <FontAwesomeIcon icon={faSpinner} spin />}
              </div>

              {/* Load More: shown only while the API reports a next page.
                  `nextPage` is null on the final page, so the control
                  disappears on its own. */}
              {!loading && pagination && pagination.nextPage ? (
                <div className="text-center mt-4">
                  <button
                    type="button"
                    className="btn btn-outline-dark"
                    disabled={loadingMore}
                    onClick={() => getSearchSpaceItems(pagination.nextPage)}
                  >
                    {loadingMore ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin /> Loading...
                      </>
                    ) : (
                      "Load more spaces"
                    )}
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <Footer />

      {showDetails.show && (
        <SpotDetailsModal
          item={showDetails.item}
          show={showDetails.show}
          closeModal={() => setShowDetails({ show: false, item: null })}
        />
      )}
    </>
  );
};

export default Search;
